import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  EexDegradedTopOfBookRetryResponseSchema,
  EexDegradedTopOfBookRetryableScopesResponseSchema,
} from "@chro/shared";
import { Database, Play, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiContract } from "../../../lib/api";
import { cn } from "../../../lib/utils";

type ApiFamily = "derivatives" | "gas_spot" | "index";
type Dataset =
  | "top_of_book"
  | "public_trades"
  | "book_events"
  | "settlements"
  | "statistics"
  | "index_publications";

type Target = {
  targetKey: string;
  apiFamily: ApiFamily;
  marketSegment: string;
  commodityCode: string;
  areaCode: string;
  dataset: Dataset;
  selector: { type: string; code: string };
  subjectLabel: string;
  cadenceMode: "interval" | "publication_window";
  snapshotPriorityAllowed: boolean;
  selectable: boolean;
  draftRegisterable: boolean;
  currentDraft: boolean;
  issues: string[];
  currentConfiguration: null | {
    scopeId: string;
    enabled: boolean;
    livePollIntervalSeconds: number | null;
    snapshotPriority: 1 | 2 | 3 | null;
    rolloutPhase: "shadow" | "live_readonly" | "history_enabled" | "quote_enabled" | null;
    conformanceRunId: string | null;
    conformanceRunState: "running" | "passed" | "failed" | "aborted" | null;
  };
};

type TargetResponse = {
  ready: boolean;
  reason: "ready" | "credential_not_validated" | "no_current_catalogue_targets";
  targets: Target[];
};

type CatalogueStatus = {
  masterEnabled: boolean;
  jobs: { queued: number; leased: number; failed: number };
  scopes: Array<{
    apiFamily: ApiFamily;
    commodity: string;
    area: string;
    state: string;
    sourceState: string;
    lastSyncAt: string | null;
  }>;
};

type Selection = {
  targetKey: string;
  livePollIntervalSeconds: number | null;
  snapshotPriority: 1 | 2 | 3 | null;
};

type Budget = {
  configuredLimitRequestsPerSecond: number;
  accepted: boolean;
  principals: Array<{
    scheduledRequestsPerSecond: number;
    reservedRequestsPerSecond: number;
    totalRequestsPerSecond: number;
    remainingRequestsPerSecond: number;
    utilization: number;
    accepted: boolean;
  }>;
};

type Preview = {
  accepted: boolean;
  newScopeCount: number;
  draftScopeCount: number;
  replayScopeCount: number;
  issues: Array<{ targetKey: string; reasons: string[] }>;
  requestBudget: Budget;
};

type StartResult = Preview & {
  conformanceRunId: string | null;
  scopes: Array<{
    targetKey: string;
    scopeId: string;
    outcome: "started_shadow" | "draft_activated" | "idempotent_replay";
    rolloutPhase: string;
  }>;
};

type DraftRegistrationResult = {
  registeredDraftCount: number;
  replayDraftCount: number;
  scopes: Array<{
    targetKey: string;
    scopeId: string;
    outcome: "draft_registered" | "idempotent_replay";
  }>;
};

export function ScopeOnboardingPanel({
  connectionReady,
}: {
  connectionReady: boolean;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Record<string, Selection>>({});
  const [search, setSearch] = useState("");
  const [family, setFamily] = useState<"all" | ApiFamily>("all");
  const [dataset, setDataset] = useState<"all" | Dataset>("all");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewSignature, setPreviewSignature] = useState<string | null>(null);
  const catalogueWasRunning = useRef(false);

  const catalogueQ = useQuery({
    queryKey: ["eex-market-data", "catalogue-status"],
    queryFn: () => api<CatalogueStatus>("/eex/market-data/catalogue/status"),
    enabled: connectionReady,
    retry: false,
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs;
      return jobs && jobs.queued + jobs.leased > 0 ? 2_000 : false;
    },
  });
  const targetsQ = useQuery({
    queryKey: ["eex-market-data", "scope-onboarding-targets"],
    queryFn: () => api<TargetResponse>("/eex/market-data/scope-onboarding/targets"),
    enabled: connectionReady,
    retry: false,
  });
  const degradedTopOfBookQ = useQuery({
    queryKey: ["eex-market-data", "degraded-top-of-book-retryable-scopes"],
    queryFn: () => apiContract(
      EexDegradedTopOfBookRetryableScopesResponseSchema,
      "/eex/market-data/scopes/retry-degraded-top-of-book"
    ),
    enabled: connectionReady,
    retry: false,
    refetchInterval: 5_000,
  });
  const catalogueJobsActive = Boolean(
    catalogueQ.data && catalogueQ.data.jobs.queued + catalogueQ.data.jobs.leased > 0
  );
  useEffect(() => {
    if (catalogueWasRunning.current && !catalogueJobsActive) {
      void queryClient.invalidateQueries({
        queryKey: ["eex-market-data", "scope-onboarding-targets"],
      });
    }
    catalogueWasRunning.current = catalogueJobsActive;
  }, [catalogueJobsActive, queryClient]);

  const selections = useMemo(
    () => Object.values(selected).sort((left, right) => left.targetKey.localeCompare(right.targetKey)),
    [selected]
  );
  const selectionSignature = JSON.stringify(selections);
  const previewM = useMutation({
    mutationFn: () =>
      api<Preview>("/eex/market-data/scope-onboarding/preview", {
        method: "POST",
        body: JSON.stringify({ scopes: selections }),
      }),
    onSuccess: (value) => {
      setError(null);
      setPreviewSignature(selectionSignature);
      setFeedback(
        value.accepted
          ? `Budget accepted for ${value.newScopeCount} new scope${value.newScopeCount === 1 ? "" : "s"}` +
              (value.draftScopeCount ? `, ${value.draftScopeCount} policy draft${value.draftScopeCount === 1 ? "" : "s"}` : "") +
              (value.replayScopeCount ? ` and ${value.replayScopeCount} unchanged replay${value.replayScopeCount === 1 ? "" : "s"}.` : ".")
          : "The selected batch cannot start safely. Review the issues below."
      );
    },
    onError: showError(setError),
  });
  const startM = useMutation({
    mutationFn: () =>
      api<StartResult>("/eex/market-data/scope-onboarding/start-shadow", {
        method: "POST",
        body: JSON.stringify({ scopes: selections }),
      }),
    onSuccess: (value) => {
      setError(null);
      setFeedback(
        value.conformanceRunId
          ? `${value.newScopeCount + value.draftScopeCount} scope${value.newScopeCount + value.draftScopeCount === 1 ? "" : "s"} started in shadow. Live pages remain gated until conformance passes.`
          : "The identical scope configuration already exists; no history or rollout gate was changed."
      );
      setSelected({});
      setPreviewSignature(null);
      previewM.reset();
      void queryClient.invalidateQueries({ queryKey: ["eex-market-data"] });
    },
    onError: showError(setError),
  });
  const draftsM = useMutation({
    mutationFn: (targetKeys: string[]) =>
      api<DraftRegistrationResult>("/eex/market-data/scope-onboarding/register-drafts", {
        method: "POST",
        body: JSON.stringify({ targetKeys }),
      }),
    onSuccess: (value) => {
      setError(null);
      setFeedback(
        value.registeredDraftCount
          ? `${value.registeredDraftCount} inert policy-setup draft${value.registeredDraftCount === 1 ? "" : "s"} registered. Configure and verify the required policies before starting shadow.`
          : `${value.replayDraftCount} selected policy-setup draft${value.replayDraftCount === 1 ? "" : "s"} already existed; nothing operational changed.`
      );
      setSelected({});
      setPreviewSignature(null);
      previewM.reset();
      void queryClient.invalidateQueries({
        queryKey: ["eex-market-data", "scope-onboarding-targets"],
      });
    },
    onError: showError(setError),
  });
  const syncM = useMutation({
    mutationFn: () =>
      api("/eex/market-data/catalogue/sync", {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      setError(null);
      setFeedback("Catalogue synchronization queued for every currently validated API family.");
      void queryClient.invalidateQueries({ queryKey: ["eex-market-data", "catalogue-status"] });
    },
    onError: showError(setError),
  });
  const degradedTopOfBookScopeIds = degradedTopOfBookQ.data?.scopeIds ?? [];
  const retryDegradedM = useMutation({
    mutationFn: () => apiContract(EexDegradedTopOfBookRetryResponseSchema, "/eex/market-data/scopes/retry-degraded-top-of-book", {
      method: "POST",
      // The server re-checks the exact current eligible scopes just before it
      // enqueues the provider-backed probes. Do not make recovery depend on
      // the much larger catalogue/history reads completing first.
      body: JSON.stringify({}),
    }),
    onSuccess: (value) => {
      setError(null);
      setFeedback(`${value.scopes.length} degraded market-feed scope${value.scopes.length === 1 ? "" : "s"} queued for an exact EEX retry.`);
      void queryClient.invalidateQueries({ queryKey: ["eex-market-data"] });
    },
    onError: showError(setError),
  });

  const targets = targetsQ.data?.targets ?? [];
  useEffect(() => {
    const refreshedTargets = targetsQ.data?.targets;
    if (!refreshedTargets) return;
    const availableTargetKeys = new Set(refreshedTargets.map((target) => target.targetKey));
    setSelected((current) => {
      const retained = Object.fromEntries(
        Object.entries(current).filter(([targetKey]) => availableTargetKeys.has(targetKey))
      );
      return Object.keys(retained).length === Object.keys(current).length ? current : retained;
    });
    // A refreshed catalogue/policy snapshot invalidates even an unchanged
    // selection's prior preview; start-shadow will always require a fresh one.
    setPreviewSignature(null);
    previewM.reset();
  }, [targetsQ.data?.targets]);
  const families = unique(targets.map((target) => target.apiFamily));
  const datasets = unique(targets.map((target) => target.dataset));
  const normalizedSearch = search.trim().toLowerCase();
  const matching = targets.filter((target) =>
    (family === "all" || target.apiFamily === family) &&
    (dataset === "all" || target.dataset === dataset) &&
    (!normalizedSearch || [
      target.commodityCode,
      target.areaCode,
      target.dataset,
      target.subjectLabel,
      target.selector.code,
    ].some((value) => value.toLowerCase().includes(normalizedSearch)))
  );
  const visible = matching.slice(0, 200);
  const currentPreview = previewSignature === selectionSignature ? previewM.data : undefined;
  const selectedTargets = selections.map((selection) =>
    targets.find((target) => target.targetKey === selection.targetKey)
  );
  const canRegisterDrafts = selectedTargets.length > 0 &&
    selectedTargets.every((target) => target?.draftRegisterable === true);
  const canStartSelection = selectedTargets.length > 0 &&
    selectedTargets.every((target) => target?.selectable === true);
  const busy = previewM.isPending || startM.isPending || draftsM.isPending || syncM.isPending || retryDegradedM.isPending;

  return (
    <section className="space-y-4 rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Database size={16} /> Acquisition scopes
          </div>
          <p className="mt-1 max-w-3xl text-xs text-muted">
            Select any scope discovered for this company&apos;s validated EEX access. New scopes acquire only in
            shadow and do not appear in Live Market, history or Quote Builder until their exact conformance gate advances.
            Targets that still need a reviewed policy can first be registered as inert setup drafts.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!connectionReady || busy}
            onClick={() => retryDegradedM.mutate()}
            className="inline-flex items-center gap-1.5 rounded border border-amber/50 px-3 py-1.5 text-sm text-amber disabled:opacity-40"
          >
            <RefreshCw size={14} className={cn(retryDegradedM.isPending && "animate-spin")} /> Retry degraded market feeds{degradedTopOfBookScopeIds.length > 0 ? ` (${degradedTopOfBookScopeIds.length})` : ""}
          </button>
          <button
            type="button"
            disabled={!connectionReady || busy}
            onClick={() => syncM.mutate()}
            className="inline-flex items-center gap-1.5 rounded border border-border px-3 py-1.5 text-sm disabled:opacity-40"
          >
            <RefreshCw size={14} className={cn(syncM.isPending && "animate-spin")} /> Sync catalogue
          </button>
        </div>
      </div>

      {!connectionReady ? (
        <div className="rounded border border-border p-3 text-xs text-muted">
          Enable and successfully test the active credential first. Scope selection does not infer any country,
          commodity, hub or product defaults.
        </div>
      ) : catalogueQ.isLoading || targetsQ.isLoading ? (
        <div className="text-xs text-muted">Loading the company&apos;s current catalogue choices…</div>
      ) : catalogueQ.isError || targetsQ.isError ? (
        <div className="text-xs text-negRed">
          Unable to load catalogue choices: {String((catalogueQ.error ?? targetsQ.error) as Error)}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
            <span>{catalogueQ.data?.scopes.length ?? 0} catalogue areas/series</span>
            <span>{targets.length} selectable dataset targets</span>
            <span>{catalogueQ.data?.jobs.queued ?? 0} queued · {catalogueQ.data?.jobs.leased ?? 0} running</span>
          </div>

          {!targetsQ.data?.ready ? (
            <div className="rounded border border-amber/40 bg-amber/5 p-3 text-xs text-amber">
              {targetsQ.data?.reason === "no_current_catalogue_targets"
                ? "No current catalogue-backed targets are available yet. Run catalogue sync and wait for it to finish."
                : "The active credential is not currently validated for scope onboarding."}
            </div>
          ) : (
            <>
              <div className="grid gap-2 sm:grid-cols-[minmax(12rem,1fr)_10rem_12rem]">
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search commodity, area, dataset or contract"
                  className="rounded border border-border bg-transparent px-2 py-1.5 text-xs focus:border-accent focus:outline-none"
                />
                <select
                  value={family}
                  onChange={(event) => setFamily(event.target.value as "all" | ApiFamily)}
                  className="rounded border border-border bg-bg px-2 py-1.5 text-xs"
                >
                  <option value="all">All API families</option>
                  {families.map((value) => <option key={value} value={value}>{label(value)}</option>)}
                </select>
                <select
                  value={dataset}
                  onChange={(event) => setDataset(event.target.value as "all" | Dataset)}
                  className="rounded border border-border bg-bg px-2 py-1.5 text-xs"
                >
                  <option value="all">All datasets</option>
                  {datasets.map((value) => <option key={value} value={value}>{label(value)}</option>)}
                </select>
              </div>

              <div className="max-h-[32rem] overflow-auto rounded border border-border">
                <table className="w-full min-w-[58rem] text-left text-xs">
                  <thead className="sticky top-0 bg-bg text-muted">
                    <tr>
                      <th className="p-2">Select</th>
                      <th className="p-2">Family</th>
                      <th className="p-2">Commodity / area</th>
                      <th className="p-2">Dataset / subject</th>
                      <th className="p-2">Poll seconds</th>
                      <th className="p-2">Snapshot storage</th>
                      <th className="p-2">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((target) => {
                      const choice = selected[target.targetKey];
                      const configured = target.currentConfiguration;
                      return (
                        <tr key={target.targetKey} className="border-t border-border align-top">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={Boolean(choice)}
                              disabled={(!target.selectable && !target.draftRegisterable) || busy}
                              onChange={(event) => {
                                setPreviewSignature(null);
                                previewM.reset();
                                setSelected((current) => {
                                  const next = { ...current };
                                  if (!event.target.checked) delete next[target.targetKey];
                                  else {
                                    next[target.targetKey] = {
                                      targetKey: target.targetKey,
                                      livePollIntervalSeconds: target.cadenceMode === "interval"
                                        ? configured?.livePollIntervalSeconds ?? 30
                                        : null,
                                      snapshotPriority: target.snapshotPriorityAllowed
                                        ? configured?.snapshotPriority ?? null
                                        : null,
                                    };
                                  }
                                  return next;
                                });
                              }}
                            />
                          </td>
                          <td className="p-2">{label(target.apiFamily)}</td>
                          <td className="p-2">
                            <div>{target.commodityCode}</div>
                            <div className="text-muted">{target.areaCode}</div>
                          </td>
                          <td className="p-2">
                            <div>{label(target.dataset)}</div>
                            <div className="max-w-xs truncate text-muted" title={target.subjectLabel}>
                              {target.subjectLabel}
                            </div>
                          </td>
                          <td className="p-2">
                            {choice && target.cadenceMode === "interval" ? (
                              <input
                                type="number"
                                min={1}
                                max={86_400}
                                value={choice.livePollIntervalSeconds ?? 30}
                                onChange={(event) => updateSelection(
                                  setSelected,
                                  target.targetKey,
                                  { livePollIntervalSeconds: Number(event.target.value) }
                                )}
                                className="w-24 rounded border border-border bg-transparent px-2 py-1"
                              />
                            ) : target.cadenceMode === "publication_window" ? (
                              <span className="text-muted">verified window</span>
                            ) : "—"}
                          </td>
                          <td className="p-2">
                            {choice && target.snapshotPriorityAllowed ? (
                              <select
                                value={choice.snapshotPriority ?? ""}
                                onChange={(event) => updateSelection(
                                  setSelected,
                                  target.targetKey,
                                  { snapshotPriority: event.target.value
                                    ? Number(event.target.value) as 1 | 2 | 3
                                    : null }
                                )}
                                className="rounded border border-border bg-bg px-2 py-1"
                              >
                                <option value="">Live only</option>
                                <option value="1">Level 1 · 5 min</option>
                                <option value="2">Level 2 · 15 min</option>
                                <option value="3">Level 3 · 1 hour</option>
                              </select>
                            ) : <span className="text-muted">{target.snapshotPriorityAllowed ? "select first" : "event data"}</span>}
                          </td>
                          <td className="p-2">
                            {target.currentDraft && configured ? (
                              <div className="space-y-1">
                                <span className="text-amber">policy setup draft</span>
                                <div className="flex max-w-sm items-center gap-1.5">
                                  <code
                                    className="select-all break-all rounded bg-border/30 px-1.5 py-1 text-[10px]"
                                    title="Acquisition scope ID required by the policy operator"
                                  >
                                    {configured.scopeId}
                                  </code>
                                  <button
                                    type="button"
                                    className="shrink-0 rounded border border-border px-1.5 py-1 text-[10px]"
                                    onClick={() => {
                                      void copyDraftScopeId(configured.scopeId).then(() => {
                                        setError(null);
                                        setFeedback("Draft scope ID copied for the policy operator.");
                                      }).catch(() => {
                                        setError("Unable to copy the draft scope ID automatically. Select the ID and copy it manually.");
                                      });
                                    }}
                                  >
                                    Copy scope ID
                                  </button>
                                </div>
                              </div>
                            ) : configured ? (
                              <span>{configured.rolloutPhase ?? "configuration incomplete"}</span>
                            ) : target.selectable ? (
                              <span className="text-muted">not configured</span>
                            ) : target.draftRegisterable ? (
                              <span className="text-amber">
                                policy setup required: {target.issues.map(label).join(", ")}
                              </span>
                            ) : (
                              <span className="text-amber">{target.issues.map(label).join(", ")}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {matching.length > visible.length && (
                <div className="text-xs text-muted">
                  Showing the first {visible.length} of {matching.length} matches. Narrow the search to reach any other discovered target.
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={busy || !canStartSelection}
                  onClick={() => previewM.mutate()}
                  className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  {previewM.isPending ? "Checking…" : "Preview request budget"}
                </button>
                <button
                  type="button"
                  disabled={busy || !canRegisterDrafts}
                  onClick={() => {
                    if (confirm(`Register ${selections.length} exact EEX target${selections.length === 1 ? "" : "s"} as inert policy-setup drafts?`)) {
                      draftsM.mutate(selections.map((selection) => selection.targetKey));
                    }
                  }}
                  className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  {draftsM.isPending ? "Registering…" : "Register policy drafts"}
                </button>
                <button
                  type="button"
                  disabled={
                    busy ||
                    !canStartSelection ||
                    !currentPreview?.accepted ||
                    selections.length === 0
                  }
                  onClick={() => {
                    if (confirm(`Start ${selections.length} exact EEX scope${selections.length === 1 ? "" : "s"} in shadow?`)) {
                      startM.mutate();
                    }
                  }}
                  className="inline-flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 text-sm text-bg disabled:opacity-40"
                >
                  <Play size={14} /> {startM.isPending ? "Starting…" : "Start in shadow"}
                </button>
                <span className="text-xs text-muted">{selections.length} selected</span>
              </div>

              {currentPreview && <BudgetPreview preview={currentPreview} />}
            </>
          )}
        </>
      )}

      {feedback && <div className="text-xs text-posGreen">{feedback}</div>}
      {error && <div className="text-xs text-negRed">{error}</div>}
    </section>
  );
}

function BudgetPreview({ preview }: { preview: Preview }) {
  return (
    <div className={cn(
      "rounded border p-3 text-xs",
      preview.accepted ? "border-posGreen/40" : "border-negRed/40"
    )}>
      <div className="font-medium">
        {preview.accepted ? "Shared request budget accepted" : "Request budget or scope validation rejected"}
      </div>
      {preview.requestBudget.principals.map((principal, index) => (
        <div key={index} className="mt-1 text-muted">
          {principal.totalRequestsPerSecond.toFixed(4)} / {preview.requestBudget.configuredLimitRequestsPerSecond.toFixed(4)} requests/s
          {` · ${(principal.utilization * 100).toFixed(1)}% used · ${principal.remainingRequestsPerSecond.toFixed(4)} remaining`}
        </div>
      ))}
      {preview.issues.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-negRed">
          {preview.issues.map((issue) => (
            <li key={issue.targetKey}>{issue.reasons.map(label).join(" · ")}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function updateSelection(
  setSelected: React.Dispatch<React.SetStateAction<Record<string, Selection>>>,
  targetKey: string,
  patch: Partial<Selection>
) {
  setSelected((current) => {
    const existing = current[targetKey];
    return existing ? { ...current, [targetKey]: { ...existing, ...patch } } : current;
  });
}

function unique<T extends string>(values: readonly T[]): T[] {
  return [...new Set(values)].sort((left, right) => left.localeCompare(right));
}

function label(value: string): string {
  return value.replaceAll("_", " ");
}

function showError(setter: (value: string | null) => void) {
  return (value: unknown) => setter(value instanceof Error ? value.message : String(value));
}

async function copyDraftScopeId(scopeId: string): Promise<void> {
  if (!navigator.clipboard?.writeText) throw new Error("Clipboard access unavailable");
  await navigator.clipboard.writeText(scopeId);
}
